import { DEFAULT_THEME, backdropLayers, logoBox, logoPaint, themeColours, type PublicSession } from "@engage/contracts";
import { useEffect, useMemo, useState } from "preact/hooks";
import { NameRefused, SessionFull, TooManyAttempts, join, lookup, type Joined } from "./api";
import type { RuntimeConfig } from "./config";
import { I18nContext, createI18n, useI18n } from "./i18n";
import { Live } from "./live";
import { Paced } from "./paced";
import { recall, remember } from "./storage";

/**
 * The participant's journey: code → name (if the session wants one) → the session.
 *
 * A code in the path (join.example.com/ABC123 — what the QR code encodes) skips the
 * first step, and a code this device has already joined skips straight to the session
 * as the same participant.
 */
type Screen =
  | { kind: "code"; error?: boolean }
  | { kind: "name"; code: string; session: PublicSession }
  | { kind: "live"; joined: Joined };

const codeFromPath = () => location.pathname.replace(/^\/+/, "").toUpperCase() || null;

/** Join codes are eight digits; typed, they read best in two groups of four. */
const CODE_DIGITS = 8;
const digitsOf = (value: string) => value.replace(/\D/g, "").slice(0, CODE_DIGITS);
const grouped = (digits: string) => (digits.length > 4 ? `${digits.slice(0, 4)} ${digits.slice(4)}` : digits);

export function App({ config }: { config: RuntimeConfig }) {
  const [screen, setScreen] = useState<Screen>(() => {
    const code = codeFromPath();
    const known = code ? recall(code) : null;
    return known ? { kind: "live", joined: known } : { kind: "code" };
  });

  const orgLocale = screen.kind === "code" ? null : screen.kind === "name" ? screen.session.defaultLocale : screen.joined.session.defaultLocale;
  const i18n = useMemo(() => createI18n(orgLocale), [orgLocale]);

  // Per-org branding: the accent colour the organisation chose, on every screen.
  const branding = screen.kind === "name" ? screen.session.branding : screen.kind === "live" ? screen.joined.session.branding : null;
  useEffect(() => {
    const style = document.documentElement.style;
    const accent = branding?.primaryColor ?? null;
    const background = branding?.background ?? null;
    // The deck's theme, derived the way the projector derives it (contracts/theme.ts), so
    // a light brand gets dark text here as it does on the screen at the front.
    const c = themeColours({ ...DEFAULT_THEME, ...(accent ? { accent } : {}), ...(background ? { background } : {}) });
    const vars: Record<string, string | null> = {
      "--accent": accent,
      "--on-accent": accent ? c.onAccent : null,
      "--stage": background,
      "--on-stage": background ? c.foreground : null,
      "--on-stage-muted": background ? c.muted : null,
    };
    for (const [name, value] of Object.entries(vars)) {
      if (value) style.setProperty(name, value);
      else style.removeProperty(name);
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", background ?? "#17132e");
  }, [branding?.primaryColor, branding?.background]);

  const enter = (joined: Joined) => {
    remember(joined);
    history.replaceState(null, "", `/${joined.code}`);
    setScreen({ kind: "live", joined });
  };

  const restart = () => {
    history.replaceState(null, "", "/");
    setScreen({ kind: "code" });
  };

  return (
    <I18nContext.Provider value={i18n}>
      {branding?.backgroundImage && <Backdrop image={branding.backgroundImage} background={branding.background} apiUrl={config.apiUrl} />}
      <main class="shell">
        {screen.kind !== "live" && <Brand />}
        {screen.kind === "code" && (
          <CodeStep
            config={config}
            initialCode={codeFromPath() ?? ""}
            onFound={(code, session) =>
              // An anonymous session collects no names, so there is nothing to ask.
              session.settings.anonymous
                ? void join(config, code).then((j) => (j ? enter(j) : setScreen({ kind: "code", error: true })))
                : setScreen({ kind: "name", code, session })
            }
          />
        )}
        {screen.kind === "name" && (
          <NameStep config={config} code={screen.code} session={screen.session} onJoined={enter} onBack={restart} />
        )}
        {screen.kind === "live" &&
          // A self-paced session is walked alone, over plain HTTP; a live one follows the room.
          (screen.joined.session.mode === "self_paced" ? (
            <Paced config={config} joined={screen.joined} onLeave={restart} />
          ) : (
            <Live config={config} joined={screen.joined} onLeave={restart} />
          ))}
      </main>
    </I18nContext.Provider>
  );
}

/** The mark and name, over the entry screens: this is the right place, before anything else. */
function Brand() {
  const { t } = useI18n();
  return (
    <header class="brand">
      <svg viewBox="0 0 32 32" aria-hidden="true">
        <rect x="0.5" y="0.5" width="31" height="31" rx="9" fill="#17132e" stroke="rgb(255 255 255 / 0.15)" />
        <rect x="7" y="7" width="8" height="8" rx="2.4" fill="#e11d48" />
        <rect x="17" y="7" width="8" height="8" rx="2.4" fill="#2563eb" />
        <rect x="7" y="17" width="8" height="8" rx="2.4" fill="#d97706" />
        <rect x="17" y="17" width="8" height="8" rx="2.4" fill="#059669" />
      </svg>
      {t("brand.name")}
    </header>
  );
}

function CodeStep({
  config,
  initialCode,
  onFound,
}: {
  config: RuntimeConfig;
  initialCode: string;
  onFound: (code: string, session: PublicSession) => void;
}) {
  const { t } = useI18n();
  const [code, setCode] = useState(() => digitsOf(initialCode));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e?: Event, value = code) => {
    e?.preventDefault();
    const clean = digitsOf(value);
    if (clean.length < CODE_DIGITS || busy) return;
    setBusy(true);
    setError(null);
    try {
      const session = await lookup(config, clean);
      if (session) onFound(clean, session);
      else setError(t("join.notFound"));
    } catch (err) {
      setError(t(err instanceof TooManyAttempts ? "join.tooMany" : err instanceof SessionFull ? "join.full" : "join.error"));
    } finally {
      setBusy(false);
    }
  };

  // Arriving from a QR code: look it up straight away.
  useEffect(() => {
    if (initialCode) void submit();
  }, []);

  const onInput = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const digits = digitsOf(input.value);
    // Written back at once, so a letter typed on a full keyboard never shows.
    input.value = grouped(digits);
    setCode(digits);
    setError(null);
    // The last digit completes the code: no reason to make anyone look for a button.
    if (digits.length === CODE_DIGITS) void submit(undefined, digits);
  };

  return (
    <form class="card stack" onSubmit={submit}>
      <h1>{t("join.title")}</h1>
      <label class="field">
        <span>{t("join.codeLabel")}</span>
        <input
          class="code-input"
          value={grouped(code)}
          onInput={onInput}
          placeholder={t("join.codePlaceholder")}
          autoComplete="off"
          spellcheck={false}
          inputMode="numeric"
          pattern="[0-9 ]*"
          maxLength={CODE_DIGITS + 1}
          autoFocus={!initialCode}
          aria-invalid={error ? true : undefined}
        />
      </label>
      {error && <p class="error" role="alert">{error}</p>}
      <button class="primary" type="submit" disabled={busy || code.length < CODE_DIGITS}>
        {busy ? t("join.checking") : t("join.continue")}
      </button>
    </form>
  );
}

function NameStep({
  config,
  code,
  session,
  onJoined,
  onBack,
}: {
  config: RuntimeConfig;
  code: string;
  session: PublicSession;
  onJoined: (j: Joined) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const required = session.settings.requireName;

  const submit = async (e: Event) => {
    e.preventDefault();
    if (required && !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const joined = await join(config, code, name.trim() || undefined);
      if (joined) onJoined(joined);
      else setError(t("join.notFound"));
    } catch (err) {
      setError(
        t(
          err instanceof SessionFull
            ? "join.full"
            : err instanceof NameRefused
              ? "join.nameRefused"
              : err instanceof TooManyAttempts
                ? "join.tooMany"
                : "join.error",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form class="card stack" onSubmit={submit}>
      {session.branding.logoUrl && <OrgLogo src={session.branding.logoUrl} style={session.branding.logoStyle} />}
      <label class="field">
        <span>{t("join.nameLabel")}</span>
        <input
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          maxLength={40}
          autoComplete="nickname"
          required={required}
          autoFocus
        />
        <small>{required ? t("join.nameHintRequired") : t("join.nameHintOptional")}</small>
      </label>
      {/* Said where the name is asked for: who will see it, and where to read more. */}
      <p class="join-notice">
        {t("join.notice")}
        {config.privacyUrl && (
          <>
            {" "}
            <a href={config.privacyUrl} target="_blank" rel="noopener noreferrer">
              {t("join.privacy")}
            </a>
          </>
        )}
      </p>
      {error && <p class="error" role="alert">{error}</p>}
      <button class="primary" type="submit" disabled={busy || (required && !name.trim())}>
        {busy ? t("join.joining") : t("join.join")}
      </button>
      <button type="button" class="link" onClick={onBack}>
        {t("join.changeCode")}
      </button>
    </form>
  );
}

const TINT = { white: "brightness(0) invert(1)", black: "brightness(0)" } as const;

/**
 * The organisation's logo on the name card, sized for its shape and backed against the
 * card's colour — which follows the phone's light or dark mode — by the rules the big
 * screen uses (contracts/logo.ts). One saved before logos were measured is shown as
 * it comes.
 */
function OrgLogo({ src, style }: { src: string; style: PublicSession["branding"]["logoStyle"] }) {
  if (!style) return <img class="logo" src={src} alt="" />;
  const surface = getComputedStyle(document.documentElement).getPropertyValue("--surface").trim();
  const box = logoBox(style);
  const paint = logoPaint(style, /^#[0-9a-f]{6}$/i.test(surface) ? surface : "#ffffff");
  const u = (n: number) => `calc(${n.toFixed(3)} * 1.6rem)`;
  return (
    <span class="logo-box" style={paint.plate ? { background: paint.plate, padding: u(box.pad), borderRadius: u(box.pad * 0.8) } : undefined}>
      <img
        src={src}
        alt=""
        style={{
          width: box.width === null ? "auto" : u(box.width),
          height: u(box.height),
          maxWidth: "100%",
          objectFit: "contain",
          filter: paint.tint ? TINT[paint.tint] : undefined,
        }}
      />
    </span>
  );
}

/**
 * The theme's picture behind everything, shaded as the projector shades it so the
 * phone's text stays readable on it (contracts/backdrop.ts). Loaded from the API, which
 * serves uploaded images to any origin.
 */
function Backdrop({
  image,
  background,
  apiUrl,
}: {
  image: NonNullable<PublicSession["branding"]["backgroundImage"]>;
  background: string | null;
  apiUrl: string;
}) {
  const layers = backdropLayers({ ...image, dark: null, light: null }, background ?? DEFAULT_THEME.background, `${apiUrl}${image.src}`);
  return <div class="backdrop" aria-hidden="true" style={layers} />;
}
